/**
 * useEngagementBeacon — the foreground active-time heartbeat (ANL-PR3).
 *
 * Mounted ONCE, high in the authenticated app (App.js), for authenticated users
 * only. It measures how long the student is ACTIVELY engaged (tab visible AND
 * window focused) and periodically flushes that active time to the session
 * ingest beacon, tagged with the CURRENT route's engagement-area.
 *
 * WHAT COUNTS AS "ACTIVE" (and why a backgrounded tab adds nothing)
 * ----------------------------------------------------------------
 * Active = `document.visibilityState === 'visible'` AND the window has focus.
 * A tab the student switched away from (hidden) or a window they clicked off of
 * (blurred) accrues ZERO active time — engagement analytics must reflect time the
 * student was actually looking at the page, not a tab left open overnight. We
 * track an "active since" timestamp (performance.now()); whenever we transition
 * OUT of active we settle (now - activeSince) into an accumulator and clear the
 * timestamp, so background time is never folded in.
 *
 * WHEN WE FLUSH
 *   • Every FLUSH_INTERVAL_MS of WALL time while a heartbeat interval runs we
 *     check the accumulator and, if it has crossed FLUSH_THRESHOLD_MS (~45s of
 *     ACTIVE time), POST it and reset the accumulator. (The interval is wall-time;
 *     the THRESHOLD is active-time — a mostly-idle session flushes rarely.)
 *   • On visibilitychange → hidden we settle + flush immediately (the student may
 *     be leaving; capture what we have). sendBeacon so it survives a teardown.
 *   • On pagehide / beforeunload we settle + flush via sendBeacon (the unload
 *     path the browser guarantees to deliver).
 * After every flush the accumulator resets to 0, so active time is counted once.
 *
 * The hook renders nothing and holds ALL timing state in refs (no re-renders) —
 * a heartbeat must be invisible to React's render path. Every send is best-effort
 * and silent (postSession swallows errors + honors the disabled and stale-token
 * latches — no token, or a token the server 401'd, means no request), so the
 * beacon can never throw into the UI and never 401-spams a logged-out page.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { postSession } from '../api/telemetry';
import { areaForPath } from './areaForPath';

// Active-time that triggers a flush. ~45s keeps the row fresh without chatter:
// a focused student flushes every 45s; an idle one rarely flushes at all.
export const FLUSH_THRESHOLD_MS = 45_000;

// Wall-clock cadence at which we re-check whether the active-time accumulator has
// crossed the threshold. 5s is fine-grained enough that a flush lands within ~5s
// of crossing 45s, while keeping the timer cheap. (We intentionally do NOT use a
// single 45s timer: active time only advances while focused, so a wall-clock 45s
// timer would flush too eagerly for a partly-idle session.)
const CHECK_INTERVAL_MS = 5_000;

/** A monotonic clock that's immune to wall-clock jumps (NTP / DST / sleep). */
function _now() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

/**
 * @param {boolean} enabled  Only true for authenticated users. When false the
 *   hook installs nothing (no listeners, no timer, no sends) — guests are never
 *   beaconed.
 */
export function useEngagementBeacon(enabled) {
  // The latest router pathname, mirrored into a ref so the interval + the unload
  // listener (both long-lived, registered once) always read the CURRENT route
  // without re-subscribing on every navigation.
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // Accumulated ACTIVE ms not yet flushed.
  const accumulatedRef = useRef(0);
  // Timestamp (monotonic) we became active, or null when inactive.
  const activeSinceRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    // Settle the elapsed active span (if currently active) into the accumulator
    // and clear the active-since marker. Idempotent: a no-op when inactive.
    const settle = () => {
      if (activeSinceRef.current != null) {
        const elapsed = _now() - activeSinceRef.current;
        if (elapsed > 0) accumulatedRef.current += elapsed;
        activeSinceRef.current = null;
      }
    };

    // Are we active right now? visible AND focused. document.hasFocus() is the
    // focus signal (true when the document has focus). visibilityState is the
    // tab-foreground signal. Both must hold.
    const isActive = () => {
      const visible =
        typeof document === 'undefined' ||
        document.visibilityState === undefined ||
        document.visibilityState === 'visible';
      const focused =
        typeof document === 'undefined' ||
        typeof document.hasFocus !== 'function' ||
        document.hasFocus();
      return visible && focused;
    };

    // Begin (or refresh) the active span if we're active and not already timing.
    const startIfActive = () => {
      if (activeSinceRef.current == null && isActive()) {
        activeSinceRef.current = _now();
      }
    };

    // Flush the accumulated active time as a session beacon tagged with the
    // current route's area, then reset the accumulator. `useBeacon` routes the
    // send through navigator.sendBeacon for the unload paths.
    const flush = (useBeacon) => {
      settle();
      const ms = Math.floor(accumulatedRef.current);
      accumulatedRef.current = 0;
      // Always send when there's anything to report OR on an unload (a 0-ms
      // unload still counts the session server-side). For the periodic path we
      // only get here past the threshold, so ms > 0.
      const subject = areaForPath(pathRef.current);
      // Fire-and-forget; postSession is silent + honors the disabled latch.
      postSession({ activeMs: ms, subject }, { useBeacon: !!useBeacon });
    };

    // ── Event wiring ──────────────────────────────────────────────────────────

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Going to background: settle + flush what we have (the student may be
        // leaving). sendBeacon so a teardown right after doesn't drop it.
        flush(true);
      } else {
        // Returning to foreground: start a fresh active span.
        startIfActive();
      }
    };

    const onFocus = () => startIfActive();
    const onBlur = () => settle(); // lost focus → stop accruing (keep the total)

    // pagehide is the modern, reliable unload signal (fires for bfcache too);
    // beforeunload is the legacy belt-and-suspenders. Both flush via sendBeacon.
    const onPageHide = () => flush(true);
    const onBeforeUnload = () => flush(true);

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);

    // Start timing immediately if we mount already-active (the common case).
    startIfActive();

    // Periodic check: settle the live span into the accumulator, and if we've
    // crossed the active-time threshold, flush (fetch path — we're still live).
    const intervalId = setInterval(() => {
      // Settle then immediately re-open the span so the accumulator reflects
      // active time up to *now* without losing the ongoing span.
      settle();
      startIfActive();
      if (accumulatedRef.current >= FLUSH_THRESHOLD_MS) {
        flush(false);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
      // On unmount (e.g. logout), flush a final time via fetch (we're still in a
      // live document, not a teardown) so the last active span isn't lost.
      flush(false);
    };
    // enabled is the only dependency — the effect installs the whole heartbeat
    // once when the user becomes authenticated and tears it down on logout. The
    // current route is read through pathRef (updated every render above), so we
    // intentionally do NOT re-run this effect on navigation.
  }, [enabled]);
}

export default useEngagementBeacon;
